/**
 * UserDeactivateModal — soft-deactivate a user with open-data handover.
 *
 * Launched from UserFormModal (edit mode). Loads the deactivation preflight
 * (open contacts / active plans / open tasks per membership company) and
 * requires a successor pick for every company that has open items. Companies
 * with nothing open render as "nic k převodu" and need no pick. Submit calls
 * POST /v1/users/:id/deactivate — the API re-validates everything in a
 * transaction, so this modal is a convenience, not the boundary.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Modal, DialogCrown } from '@/components/Modal';
import { SelectInput } from '@/components/SelectInput';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import {
  useCompanyMembersQuery,
  useDeactivateUserMutation,
  useDeactivationPreflightQuery,
  type DeactivationPreflightFirma,
  type MgmtUser,
} from './api';

interface UserDeactivateModalProps {
  open: boolean;
  user?: MgmtUser;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful deactivation (closes the parent form too). */
  onDeactivated: () => void;
}

export function UserDeactivateModal({
  open,
  user,
  onOpenChange,
  onDeactivated,
}: UserDeactivateModalProps) {
  const preflight = useDeactivationPreflightQuery(open && user ? user.id_uzivatele : null);
  const deactivate = useDeactivateUserMutation();
  const [nastupci, setNastupci] = useState<Record<number, number>>({});

  useEffect(() => {
    if (open) setNastupci({});
  }, [open, user?.id_uzivatele]);

  const firmy = preflight.data?.firmy ?? [];
  const needy = firmy.filter((f) => f.vyzaduje_nastupce);
  const allPicked = needy.every((f) => nastupci[f.id_firmy] > 0);

  const submit = () => {
    if (!user || !allPicked) return;
    deactivate.mutate(
      {
        id: user.id_uzivatele,
        body: {
          nastupci: needy.map((f) => ({
            id_firmy: f.id_firmy,
            id_uzivatele: nastupci[f.id_firmy],
          })),
        },
      },
      {
        onSuccess: () => {
          toast.ok('Uživatel deaktivován');
          onOpenChange(false);
          onDeactivated();
        },
        onError: (err) => toast.error(apiMessage(err, 'Deaktivace se nepodařila.')),
      },
    );
  };

  const fullName = user ? [user.jmeno, user.prijmeni].filter(Boolean).join(' ') || user.nick : '';

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={640}>
      <DialogCrown
        tone="danger"
        eyebrow="Deaktivace uživatele"
        eyebrowMeta={user ? `#${user.id_uzivatele}` : undefined}
        title={
          <>
            Deaktivovat <em>{fullName}</em>.
          </>
        }
        sub="Uživatel se nebude moci přihlásit a zmizí ze všech výběrů. Uzavřená historie mu zůstane; rozjednané kontakty, aktivní plány a otevřené úkoly převezme nástupce."
      />
      <Modal.Body className="px-8 py-6">
        {preflight.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : preflight.isError ? (
          <p className="font-mono text-[11px] text-ink-3">Přehled se nepodařilo načíst.</p>
        ) : (
          <ul className="divide-y divide-line border border-line bg-paper">
            {firmy.map((f) => (
              <FirmaRow
                key={f.id_firmy}
                firma={f}
                excludeUserId={user?.id_uzivatele ?? 0}
                value={nastupci[f.id_firmy] ?? 0}
                onChange={(id) => setNastupci((s) => ({ ...s, [f.id_firmy]: id }))}
              />
            ))}
            {firmy.length === 0 && (
              <li className="px-4 py-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                Žádná členství ve firmách.
              </li>
            )}
          </ul>
        )}
      </Modal.Body>
      <Modal.Foot
        meta={
          <span>
            <b>Deaktivace</b> okamžitě odhlásí uživatele ze všech zařízení. Účet lze později znovu
            aktivovat.
          </span>
        }
        actions={
          <>
            <Button variant="secondary" size="md" onClick={() => onOpenChange(false)}>
              Zrušit
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={submit}
              disabled={preflight.isLoading || preflight.isError || !allPicked || deactivate.isPending}
            >
              {deactivate.isPending ? 'Deaktivuji…' : 'Deaktivovat uživatele'}
            </Button>
          </>
        }
      />
    </Modal>
  );
}

interface FirmaRowProps {
  firma: DeactivationPreflightFirma;
  excludeUserId: number;
  value: number;
  onChange: (userId: number) => void;
}

function FirmaRow({ firma, excludeUserId, value, onChange }: FirmaRowProps) {
  // Only fetch the member list when a successor is actually needed.
  const members = useCompanyMembersQuery(firma.vyzaduje_nastupce ? firma.id_firmy : null);
  const candidates = (members.data?.memberships ?? []).filter(
    (m) => m.id_uzivatele !== excludeUserId && m.aktivni !== 0,
  );

  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex items-baseline gap-3 max-sm:flex-wrap">
        <span className="font-mono text-[11px] text-ink-3 tabular-nums">#{firma.id_firmy}</span>
        <span className="truncate text-[12.5px] text-ink">{firma.nazev}</span>
        <span className="ml-auto font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {firma.vyzaduje_nastupce
            ? `${firma.kontakty} kontaktů · ${firma.plany} plánů · ${firma.ukoly} úkolů`
            : 'nic k převodu'}
        </span>
      </div>
      {firma.vyzaduje_nastupce && (
        <Field label="Nástupce" required>
          <SelectInput
            value={value || ''}
            disabled={members.isLoading}
            onChange={(e) => onChange(Number(e.target.value) || 0)}
          >
            <option value="">
              {members.isLoading ? 'Načítání…' : 'Vyberte nástupce…'}
            </option>
            {candidates.map((m) => (
              <option key={m.id_uzivatele} value={m.id_uzivatele}>
                {[m.jmeno, m.prijmeni].filter(Boolean).join(' ') || m.nick || `#${m.id_uzivatele}`}
              </option>
            ))}
          </SelectInput>
        </Field>
      )}
    </li>
  );
}

function apiMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const e = (err.body as { error?: string }).error;
    if (typeof e === 'string') return e;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
